import { useMemo } from 'react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import {
  extractNormalizedValues,
  scoreItemAgainstValues,
  SearchOptions,
  stringHaystack,
  tokenizeQuery
} from './search';

interface UseFilteredListOptions<T> extends SearchOptions<T> {
  // Debounce delay in ms. Default: 150.
  debounceMs?: number;
}

// Derive a stable primitive from `keys` so callers who inline `{ keys: ['name'] }`
// don't invalidate haystack memoization on every render. Inline arrow-fn
// extractors produce identical Function.toString() output across renders even
// when their reference identity changes, so they compare stably here.
//
// Caveat: two extractors with identical source text but different *captured*
// values collide — e.g. `item => item[dynamicKey]` reuses a stale haystack when
// `dynamicKey` changes. No current consumer does this. If you need a
// dynamic-key extractor, either bake the captured value into `items` before
// passing them in, or use a string key from `keyof T`.
const keySignatureOf = <T>(keys: SearchOptions<T>['keys']): string =>
  keys.map(key => (typeof key === 'function' ? key.toString() : String(key))).join('\u0000');

export const useFilteredList = <T>(
  items: readonly T[],
  query: string,
  { keys, rank = true, limit, debounceMs = 150 }: UseFilteredListOptions<T>
): T[] => {
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  const keySignature = keySignatureOf(keys);

  // Precompute the normalized haystack once per (items, keys) change — the
  // per-keystroke work then reduces to normalizing the (short) query and doing
  // string comparisons against pre-normalized values.
  const precomputed = useMemo(
    () => items.map(item => ({ item, values: extractNormalizedValues(item, keys) })),
    // keySignature captures keys' semantic identity so inline arrays are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, keySignature]
  );

  return useMemo(() => {
    const tokens = tokenizeQuery(debouncedQuery);
    // Empty-query same-reference return — see searchItems for rationale.
    if (tokens.length === 0) return items as T[];

    const scored: { item: T; score: number }[] = [];
    for (const { item, values } of precomputed) {
      const score = scoreItemAgainstValues(values, tokens);
      if (score > 0) {
        scored.push({ item, score });
      }
    }

    // PERF NOTE: sorts the full match set even when `limit` is set. Fine at
    // current scale; revisit as a top-K heap if catalogs get large.
    if (rank) {
      scored.sort((a, b) => b.score - a.score);
    }

    const result = scored.map(({ item }) => item);
    return typeof limit === 'number' ? result.slice(0, limit) : result;
  }, [debouncedQuery, precomputed, items, rank, limit]);
};

// Hook counterpart of searchStrings — for the common "filter a list of raw
// strings" case where each item is itself the haystack.
export const useFilteredStrings = (
  items: readonly string[],
  query: string,
  opts: Omit<UseFilteredListOptions<string>, 'keys'> = {}
): string[] => useFilteredList(items, query, { ...opts, keys: [stringHaystack] });
